import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { isValid, getMonth, getYear } from 'date-fns';
import { Tooltip } from '@grandvisionhq/common';
import { Icon } from '@grandvisionhq/icons';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { DateUtils } from '@grandvisionhq/utils';
import { useFormContext } from '../Form/Form.js';
import ReactDatePicker from './ReactDatePicker.js';

const createCustomHeader = (dateFnsLocale)=>{
    return function({ date , decreaseMonth , increaseMonth , prevMonthButtonDisabled , nextMonthButtonDisabled  }) {
        var ref;
        return /*#__PURE__*/ React.createElement("div", {
            className: "react-datepicker__custom-nav"
        }, /*#__PURE__*/ React.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--previous",
            onClick: decreaseMonth,
            disabled: prevMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: "chevron-left"
        })), /*#__PURE__*/ React.createElement("div", {
            className: "react-datepicker__current-month"
        }, (dateFnsLocale === null || dateFnsLocale === void 0 ? void 0 : (ref = dateFnsLocale.localize) === null || ref === void 0 ? void 0 : ref.month(getMonth(date))) || getMonth(date), " ", getYear(date)), /*#__PURE__*/ React.createElement("button", {
            className: "react-datepicker__navigation react-datepicker__navigation--next",
            onClick: increaseMonth,
            disabled: nextMonthButtonDisabled,
            type: "button"
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: "chevron-right"
        })));
    };
};
const DatePicker = ({ onChange , onBlur , className , label , tooltip , placeholder , optionalText , errorMessage , initialValue , minDate , maxDate , dateFormat , name , disabled =false , required =true , hideAsteriskSign =false , useNativeInput =false , 'data-t': testId = 'date-picker' , autoComplete =true , touched =false , valid =true  })=>{
    const { getLabel , locale , dateFnsLocale  } = useIntl();
    const { featureConfigs  } = useFeatureToggles();
    const { datePickerDateFormat  } = featureConfigs;
    const [date1, setDate] = React.useState(initialValue);
    const [isValid$1, setIsValid] = React.useState(valid);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [isTouched, setIsTouched] = React.useState(touched);
    const customHeader = React.useMemo(()=>createCustomHeader(dateFnsLocale)
    , [
        dateFnsLocale
    ]);
    const handleKeyDown = React.useCallback((event)=>{
        if (event.key === 'Escape') {
            event.stopPropagation();
            setHasFocus(false);
        }
    }, [
        setHasFocus
    ]);
    const handleBlur = React.useCallback(()=>{
        setHasFocus(false);
        onBlur && onBlur();
    }, [
        onBlur
    ]);
    const handleFocus = React.useCallback(()=>{
        if (!disabled) {
            setHasFocus(true);
        }
    }, [
        disabled
    ]);
    const handleChange = React.useCallback((date)=>{
        setDate(date || undefined);
        !isTouched && setIsTouched(true);
    }, [
        isTouched
    ]);
    const onNativeChange = React.useCallback((e)=>{
        if (!e.target.value) {
            /*
         * Clearing the input is not supported by the current interfaces,
         * fallback to the current value. This shouldn't happen since
         * we hide the clear button with css as well.
         */ e.target.value = date1 && DateUtils.toLocalDate(date1);
            return;
        }
        handleChange(new Date(e.target.value));
    }, [
        handleChange
    ]);
    // Allow external control of the date picker, for example to reset the date by setting initialValue to undefined
    React.useEffect(()=>{
        setDate(initialValue);
        setIsTouched(touched);
        setIsValid(valid);
    }, [
        initialValue,
        touched,
        valid
    ]);
    React.useEffect(()=>{
        if (date1 && isValid(date1)) {
            onChange(date1);
            setHasFocus(false);
            isTouched && setIsValid(true);
        } else if (required) {
            isTouched && setIsValid(false);
        }
    }, [
        date1,
        onChange,
        required,
        isTouched
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('form-control date-picker', className, {
            'date-picker--focus': hasFocus,
            'date-picker--disabled': disabled
        }),
        "data-t": testId
    }, label && /*#__PURE__*/ React.createElement("label", {
        className: "form-control__label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltip && /*#__PURE__*/ React.createElement(Tooltip, {
        message: tooltip,
        id: "date-picker-tooltip"
    })), !required && optionalText && /*#__PURE__*/ React.createElement("small", {
        className: "form-control__optional-tag"
    }, optionalText), /*#__PURE__*/ React.createElement("div", {
        className: "date-picker__input-container"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "calendar"
    }), useNativeInput ? /*#__PURE__*/ React.createElement("input", {
        type: "date",
        className: classnames('form-control__input', 'date-picker__input', 'date-picker__input--native', {
            _is_invalid: !isValid$1 && isTouched,
            _is_valid: isValid$1 && isTouched
        }),
        value: date1 && DateUtils.toLocalDate(date1),
        min: minDate && DateUtils.toLocalDate(minDate),
        max: maxDate && DateUtils.toLocalDate(maxDate),
        onClick: handleFocus,
        onChange: onNativeChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        disabled: disabled,
        required: required,
        "aria-required": required,
        "aria-label": getLabel('common.selectDate'),
        name: name,
        autoComplete: autoComplete ? 'on' : 'off'
    }) : dateFnsLocale && /*#__PURE__*/ React.createElement(ReactDatePicker, {
        dateFnsLocale: dateFnsLocale,
        className: classnames('form-control__input', 'date-picker__input', {
            _is_invalid: !isValid$1 && isTouched,
            _is_valid: isValid$1 && isTouched
        }),
        selected: date1,
        onFocus: handleFocus,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        placeholderText: placeholder,
        dateFormat: dateFormat !== null && dateFormat !== void 0 ? dateFormat : datePickerDateFormat,
        locale: locale,
        minDate: minDate,
        maxDate: maxDate,
        disabled: disabled,
        renderCustomHeader: customHeader,
        name: name,
        customInput: /*#__PURE__*/ React.createElement("input", {
            name: name,
            placeholder: placeholder,
            "aria-label": getLabel('common.selectDate')
        }),
        autoComplete: autoComplete ? 'on' : 'off'
    }), /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-down"
    })), errorMessage && !isValid$1 && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__error"
    }, errorMessage));
};
// Connected components have a required name and optional onChange prop as those are handled via FormControl
const ConnectedDatePicker = (props)=>{
    const { onChange , getInitialState  } = useFormContext();
    const formState = getInitialState === null || getInitialState === void 0 ? void 0 : getInitialState(props.name);
    const value = formState === null || formState === void 0 ? void 0 : formState.value;
    const initialValueAsDate = value ? new Date(value) : undefined;
    // Context object changes on every render, make sure we just target onChange to prevent infinite renders in the original component
    const handleChange = React.useCallback((date)=>{
        const localDate = DateUtils.toLocalDate(date);
        if (localDate !== value) {
            onChange === null || onChange === void 0 ? void 0 : onChange({
                value: localDate,
                name: props.name,
                isValid: true,
                isTouched: true
            });
        }
    }, [
        value,
        onChange,
        props.name
    ]);
    return /*#__PURE__*/ React.createElement(DatePicker, swcHelpers.extends({}, props, {
        onChange: handleChange,
        initialValue: initialValueAsDate || props.initialValue,
        touched: (formState === null || formState === void 0 ? void 0 : formState.isTouched) || props.touched,
        valid: (formState === null || formState === void 0 ? void 0 : formState.isValid) || props.valid
    }));
};

export { ConnectedDatePicker, DatePicker, createCustomHeader, DatePicker as default };
//# sourceMappingURL=DatePicker.js.map
